import { z } from "zod";

import { fileSchema } from "@lendflow/schemas";

import type { IClient } from "@/models/clients";

/**
 * A stip as it's defined in settings.
 * (Previously `IStip`)
 */
export type StipDefinition = {
  id: number;
  name: string;
  description: string | null;
  file_type: string;
  weight: number;
  is_dynamic: boolean;
  dynamic_type: string | null;
  client: Pick<IClient, "id" | "name"> | null;
};

export const fieldStipSchema = z.object({
  id: z.number(),
  count: z.number(),
  description: z.string().nullable().optional(),
  is_optional: z.boolean().nullable().default(false),
});

/**
 * A stip as it's being created or edited within the stips modal
 *
 * `id` is the **stip definition** this stip should reference
 *
 * Essentially, this is the shape the api expects to **create**
 * an application/offer/checkout stip. The api then returns a
 * `DealStip` from this.
 *
 * @see `App\Http\Requests\ApplicationStipsRequest` on the api
 */
export type FieldStip = z.infer<typeof fieldStipSchema>;

export const applicationStipSchema = z.object({
  id: z.number(),
  stip_id: z.number(),
  name: z.string(),
  file_type: z.string(),
  description: z.string().nullable().optional(),
  count: z.number(),
  is_optional: z.boolean().nullable().default(false),
  files: z.array(fileSchema),
});

export const offerStipSchema = applicationStipSchema;

export const checkoutStipSchema = applicationStipSchema;

/**
 * A stip assigned to an application.
 *
 * Unlinke `FieldStip`, this `id` refers to an incrementing database row.
 * See `stip_id` for the reference stip definition id.
 */
export type ApplicationStip = z.infer<typeof applicationStipSchema>;

/**
 * A stip assigned to an offer of an application.
 *
 * Unlinke `FieldStip`, this `id` refers to an incrementing database row.
 * See `stip_id` for the reference stip definition id.
 */
export type OfferStip = ApplicationStip;

/**
 * A stip assigned to checkout template.
 *
 * Here, `id` refers to the reference stip id.
 */
export type CheckoutStip = ApplicationStip;

/**
 * A stip assigned to an application, offer, or checkout template.
 */
export type DealStip = ApplicationStip | OfferStip | CheckoutStip;

/**
 * Any of the shapes a stip can take.
 * Use with caution :)
 */
export type AnyStip = DealStip | FieldStip | StipDefinition;

export const isDealStip = (stip: AnyStip): stip is DealStip => {
  return "stip_id" in stip;
};

export const isFieldStip = (stip: AnyStip): stip is FieldStip => {
  return !("stip_id" in stip) && "count" in stip;
};

export const isStipDefinition = (stip: AnyStip): stip is StipDefinition => {
  return !("stip_id" in stip) && !("count" in stip);
};

/**
 * Normalizes other stip types into field stips so
 * the stips editor component can properly work with them.
 */
export const toFieldStip = (stip: AnyStip): FieldStip => {
  if (isDealStip(stip)) {
    return {
      id: stip.stip_id,
      description: stip.description,
      count: stip.count,
      is_optional: stip.is_optional ?? false,
    };
  }

  if (isStipDefinition(stip)) {
    return {
      id: stip.id,
      description: stip.description,
      count: 1,
      is_optional: false,
    };
  }

  if (isFieldStip(stip)) {
    return stip;
  }

  throw new Error("Invalid stip shape encountered");
};
